import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { onSnapshot } from 'firebase/firestore';
import type { ShareRole } from '@easymoving/types';
import { useAuth } from './AuthContext';
import { collaboratorDoc } from '../lib/data/paths';

/**
 * The active workspace: whose move the app is showing. Defaults to the user's
 * own; a collaborator can switch to a move that was shared with them. The
 * choice persists per device. While in a shared move we keep a live snapshot
 * on our own collaborator doc — it validates access on load, picks up role
 * changes, and *immediately* bounces us back to our own move when the owner
 * revokes. `canEdit` is the single UI gate for mutations — Firestore rules
 * remain the hard server-side guard.
 */
interface SharedTarget {
  ownerUid: string;
  ownerName?: string;
  role: ShareRole;
}

interface WorkspaceValue {
  /** Uid whose data the app reads/writes (own uid when not in a shared move). */
  activeOwnerUid: string | null;
  isOwn: boolean;
  role: 'owner' | ShareRole;
  canEdit: boolean;
  ownerName?: string;
  enterShared: (target: SharedTarget) => void;
  exitShared: () => void;
}

const KEY = 'easymoving.workspace';
const WorkspaceCtx = createContext<WorkspaceValue | null>(null);

function readPersisted(myUid: string): SharedTarget | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<SharedTarget & { myUid: string }>;
    // Stored for a different account (or malformed) → ignore.
    if (!p || p.myUid !== myUid || typeof p.ownerUid !== 'string') return null;
    return {
      ownerUid: p.ownerUid,
      ownerName: typeof p.ownerName === 'string' ? p.ownerName : undefined,
      role: p.role === 'editor' ? 'editor' : 'viewer',
    };
  } catch {
    return null;
  }
}

function clearPersisted() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [shared, setShared] = useState<SharedTarget | null>(null);

  // Restore the persisted workspace when the signed-in user changes.
  useEffect(() => {
    setShared(user ? readPersisted(user.uid) : null);
  }, [user]);

  // Live guard: watch my collaborator doc while in a shared move. Missing doc
  // or permission-denied (revoked) → back to my own move; role edits apply live.
  useEffect(() => {
    if (!user || !shared) return;
    const bail = () => {
      setShared(null);
      clearPersisted();
    };
    return onSnapshot(
      collaboratorDoc(shared.ownerUid, user.uid),
      (snap) => {
        if (!snap.exists()) return bail();
        const role = (snap.data() as { role?: string }).role === 'editor' ? 'editor' : 'viewer';
        setShared((cur) => (cur && cur.role !== role ? { ...cur, role } : cur));
      },
      bail,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-subscribe only when the target move changes
  }, [user, shared?.ownerUid]);

  const enterShared = useCallback(
    (target: SharedTarget) => {
      setShared(target);
      try {
        localStorage.setItem(KEY, JSON.stringify({ ...target, myUid: user?.uid }));
      } catch {
        /* ignore */
      }
    },
    [user?.uid],
  );

  const exitShared = useCallback(() => {
    setShared(null);
    clearPersisted();
  }, []);

  const value = useMemo<WorkspaceValue>(() => {
    const isOwn = !shared;
    return {
      activeOwnerUid: shared ? shared.ownerUid : (user?.uid ?? null),
      isOwn,
      role: shared ? shared.role : 'owner',
      canEdit: isOwn || shared?.role === 'editor',
      ownerName: shared?.ownerName,
      enterShared,
      exitShared,
    };
  }, [shared, user?.uid, enterShared, exitShared]);

  return <WorkspaceCtx.Provider value={value}>{children}</WorkspaceCtx.Provider>;
}

export function useWorkspace(): WorkspaceValue {
  const v = useContext(WorkspaceCtx);
  if (!v) throw new Error('WorkspaceProvider not mounted');
  return v;
}
